import { incomeTaxYear } from "@stb/jurisdiction-au/income-tax"

import { displayYear, formatCheckedOn } from "../components/home/home-content"
import { formatAud, SimpleTable, SourceLine } from "../components/landing"
import { ATO_MEDICARE_CALCULATOR_URL, PayCalculator } from "../components/landing/pay-calculator"
import { DEFAULT_FY, variantAnswer } from "../components/landing/pay-calculator-model"
import type { Guide } from "./guide-types"

/** Served at /medicare-levy-calculator (T917). Copy source: docs/design/html/flows/web-landing-tool-medicare.mjs and docs/specs/pay-calculator-spec.md section 9. */
const year = incomeTaxYear(DEFAULT_FY)
const fy = displayYear(DEFAULT_FY)
const medicare = year.medicare
const checked = formatCheckedOn(medicare.source.checkedOn)
const provisional = medicare.status !== "published"

const dollars = (n: number) => `A$${n.toLocaleString("en-AU")}`
const rate = medicare.rateBp / 100
const shadeIn = medicare.shadeInBp / 100
const lower = dollars(medicare.singleLower)
const upper = dollars(medicare.singleUpper)

const thresholdRows = [
  { header: `Up to ${lower}`, cells: ["Nil"] },
  {
    header: `${dollars(medicare.singleLower + 1)} – ${upper}`,
    cells: [`${shadeIn}c for each A$1 over ${lower}`],
  },
  { header: `Over ${upper}`, cells: [`${rate}% of all your taxable income`] },
]

export const medicareGuide: Guide = {
  slug: "medicare-levy-calculator",
  title: `Medicare Levy Calculator ${fy}`,
  description: `Work out your Medicare levy for ${fy} from your income, including the low-income reduction. Per year or fortnight, with ATO sources. Free.`,
  crumb: "Medicare levy calculator",
  h1: `Medicare levy calculator (${fy})`,
  published: "2026-10-30",
  reviewed: "2026-10-30",
  badge: "Free tool",
  financialYear: fy,
  checked,
  sources: [
    { label: "ATO, Medicare levy reduction for low-income earners", href: medicare.source.url },
    { label: "ATO Medicare levy calculator", href: ATO_MEDICARE_CALCULATOR_URL },
  ],
  answer: variantAnswer("medicare", formatAud),
  answerMore: (
    <>
      The calculator applies the {rate}% Medicare levy and the low-income reduction for a single
      person, using ATO thresholds, and shows the levy next to your income tax and take-home pay.
      Families, SAPTO and the surcharge are not calculated here. Enter your own pay below.
    </>
  ),
  related: [
    { label: "Pay calculator: your take-home pay after tax", href: "/pay-calculator" },
    {
      label: "HECS-HELP repayment calculator: your compulsory repayment",
      href: "/hecs-repayment-calculator",
    },
    { label: "Cents per km calculator: your car expense claim", href: "/cents-per-km-calculator" },
    { label: "Invoicing for sole traders: send invoices, see who has paid", href: "/invoicing" },
  ],
  toc: [
    { id: "calculator", label: "Work out your levy" },
    { id: "thresholds", label: "Low-income thresholds" },
    { id: "not-covered", label: "What this does not cover" },
    { id: "faq", label: "Common questions" },
  ],
  cta: {
    title: "Earning on an ABN too? Keep the books in one place",
    description:
      "The levy is worked out on your whole taxable income, including ABN profit. Kestly keeps your invoices, receipts and trips ready for tax time.",
    ctaLabel: "Start free",
    ctaHref: "/login?from=medicare-levy-calculator",
  },
  body: (
    <>
      <section aria-labelledby="calculator" className="grid gap-4">
        <h2 id="calculator" className="text-2xl font-semibold">
          Work out your levy
        </h2>
        <PayCalculator variant="medicare" />
      </section>

      <section aria-labelledby="thresholds" className="grid gap-3">
        <h2 id="thresholds" className="text-2xl font-semibold">
          Medicare levy low-income thresholds
        </h2>
        <p className="max-w-[70ch]">
          The levy is {rate}% of your taxable income. If your income is low you pay less, or none.
          The table shows the thresholds for a single person with no dependants.
        </p>
        <SimpleTable
          caption={`Medicare levy low-income thresholds, single person, ${fy}`}
          columns={["Taxable income", "Medicare levy"]}
          rows={thresholdRows}
        />
        {provisional && (
          <p className="max-w-[70ch] text-sm">
            The ATO has not yet published the {fy} thresholds. The table and the calculator use the
            2025–26 values until it does.
          </p>
        )}
        <SourceLine
          sources={[{ label: "ATO", href: medicare.source.url }]}
          financialYear={fy}
          checked={checked}
        />
      </section>

      <section aria-labelledby="not-covered" className="grid max-w-[70ch] gap-3">
        <h2 id="not-covered" className="text-2xl font-semibold">
          What this calculator does not cover
        </h2>
        <p>
          Families, seniors and pensioners (SAPTO) and the Medicare levy surcharge are not
          calculated here. The thresholds are different and depend on your family and on private
          hospital cover. Use the{" "}
          <a href={ATO_MEDICARE_CALCULATOR_URL} className="underline underline-offset-2">
            ATO Medicare levy calculator
          </a>{" "}
          for those.
        </p>
        <p>
          The levy is worked out on taxable income for the whole year. The standard deduction is
          left out unless you tick it, so your figure can differ if you have other deductions.
        </p>
      </section>
    </>
  ),
  faq: [
    {
      question: "What is the Medicare levy rate?",
      answer: `The Medicare levy is ${rate}% of your taxable income. People on low incomes pay less, or none.`,
    },
    {
      question: "Who pays no Medicare levy?",
      answer: `A single person with taxable income up to ${lower} pays no levy under the thresholds we use. Some people are exempt for other reasons, for example certain foreign residents.`,
    },
    {
      question: "How does the low-income reduction work?",
      answer: `Between ${lower} and ${upper} the levy is ${shadeIn}c for each A$1 over ${lower}, so it grows gradually until it reaches the full ${rate}% once taxable income is above ${upper}.`,
    },
    {
      question: "Is the Medicare levy the same as private health insurance?",
      answer:
        "No. The levy funds Medicare and is paid through tax. Private hospital cover is separate, and not having it can lead to the Medicare levy surcharge, which this calculator does not work out.",
    },
    {
      question: "What is the Medicare levy surcharge?",
      answer:
        "An extra charge for higher earners without appropriate private hospital cover. It depends on your income, family and cover. The ATO Medicare levy calculator works it out.",
    },
    {
      question: "Do families have different thresholds?",
      answer:
        "Yes. Families have higher thresholds that grow with each child, and seniors have their own. This page uses the single-person thresholds only.",
    },
    {
      question: "Do foreign residents pay the Medicare levy?",
      answer:
        "Generally no. Choose Foreign resident under More options to see the result without the levy. Working holiday makers are treated as provisional until the ATO says more.",
    },
    {
      question: "Is the levy on top of income tax?",
      answer:
        "Yes. It is a separate amount added to your income tax, which is why the take-home table lists both.",
    },
    {
      question: "Does the levy apply to ABN income?",
      answer:
        "Yes. It is worked out on your total taxable income, including profit from an ABN. No levy is withheld from ABN payments, so set money aside.",
    },
  ],
}
